"use client";

import { useLenis } from "lenis/react";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { AnimatePresence, motion, type PanInfo } from "motion/react";
import { useTranslations } from "next-intl";
import { Dialog as DialogPrimitive } from "radix-ui";
import { type KeyboardEvent, type ReactNode, useEffect, useState } from "react";

import { EASE_OUT_EXPO } from "@/components/motion/reveal";
import { Image } from "@/components/shared/image";
import type { MediaAsset } from "@/lib/media";
import { cn, padNumber } from "@/lib/utils";

// =============================================================================

/** Drag distance (px) or velocity (px/s) that changes the screenshot */
const SWIPE_OFFSET = 80;
const SWIPE_VELOCITY = 450;

const slideVariants = {
  enter: (direction: number) => ({
    x: `${direction * 14}%`,
    opacity: 0,
    scale: 0.96,
  }),
  center: { x: "0%", opacity: 1, scale: 1 },
  exit: (direction: number) => ({
    x: `${direction * -14}%`,
    opacity: 0,
    scale: 0.96,
  }),
};

// =============================================================================

type LightboxProps = {
  projectName: string;
  images: MediaAsset[];
  /** Index of the open screenshot, null when closed */
  index: number | null;
  onIndexChange: (index: number | null) => void;
};

/**
 * Full-screen viewer of the gallery: arrow keys, swipe and thumbnails to
 * browse, Escape or the close button to leave (focus returns to the
 * screenshot that opened it).
 */
export function Lightbox({
  projectName,
  images,
  index,
  onIndexChange,
}: LightboxProps) {
  const t = useTranslations("Projects.detail");
  const tCommon = useTranslations("Common");
  const lenis = useLenis();
  const [direction, setDirection] = useState(1);
  const isOpen = index !== null;
  const count = images.length;

  // Freeze the smooth scroll of the page behind the viewer
  useEffect(() => {
    if (!isOpen) return;
    lenis?.stop();
    return () => lenis?.start();
  }, [isOpen, lenis]);

  const go = (step: number) => {
    if (index === null || count < 2) return;
    setDirection(step);
    onIndexChange((index + step + count) % count);
  };

  const jump = (target: number) => {
    if (index === null || target === index) return;
    setDirection(target > index ? 1 : -1);
    onIndexChange(target);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") {
      event.preventDefault();
      go(1);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      go(-1);
    }
  };

  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -SWIPE_OFFSET || info.velocity.x < -SWIPE_VELOCITY) {
      go(1);
    } else if (
      info.offset.x > SWIPE_OFFSET ||
      info.velocity.x > SWIPE_VELOCITY
    ) {
      go(-1);
    }
  };

  // Uploads often default their alt text to the project name: be specific
  const screenshotAlt = (item: MediaAsset, itemIndex: number) =>
    item.alt && item.alt !== projectName
      ? item.alt
      : t("screenshot", { index: itemIndex + 1, name: projectName });

  const image = index !== null ? images[index] : null;

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => !open && onIndexChange(null)}
    >
      <AnimatePresence>
        {isOpen && image && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay forceMount asChild>
              <motion.div
                className="fixed inset-0 z-120 bg-background/90 backdrop-blur-xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.35 } }}
                transition={{ duration: 0.45, ease: EASE_OUT_EXPO }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content
              forceMount
              asChild
              onKeyDown={onKeyDown}
              aria-describedby="lightbox-hint"
            >
              <motion.div
                data-lenis-prevent
                className="fixed inset-0 z-121 flex flex-col outline-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.3 } }}
              >
                <DialogPrimitive.Title className="sr-only">
                  {t("lightbox.title", { name: projectName })}
                </DialogPrimitive.Title>
                <p id="lightbox-hint" className="sr-only">
                  {t("lightbox.hint")}
                </p>

                {/* Top bar */}
                <div className="flex items-center justify-between px-5 pt-5 sm:px-8 sm:pt-6">
                  <p
                    className="font-mono text-sm text-muted-foreground tabular-nums"
                    aria-live="polite"
                  >
                    <span className="text-foreground">
                      {padNumber(index + 1)}
                    </span>{" "}
                    / {padNumber(count)}
                  </p>
                  <DialogPrimitive.Close
                    aria-label={tCommon("close")}
                    className="group grid size-11 place-items-center rounded-full border border-foreground/15 bg-surface-1/80 transition-transform duration-300 ease-out-expo hover:scale-105"
                  >
                    <X className="size-5 transition-transform duration-500 ease-out-expo group-hover:rotate-90" />
                  </DialogPrimitive.Close>
                </div>

                {/* Stage */}
                <div className="relative min-h-0 flex-1">
                  <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                      key={index}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.6, ease: EASE_OUT_EXPO }}
                      drag={count > 1 ? "x" : false}
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.55}
                      onDragEnd={onDragEnd}
                      className="absolute inset-x-5 inset-y-4 cursor-grab touch-pan-y active:cursor-grabbing sm:inset-x-24 sm:inset-y-6"
                    >
                      <Image
                        src={image.url}
                        alt={screenshotAlt(image, index)}
                        fill
                        sizes="90vw"
                        draggable={false}
                        className="object-contain select-none"
                        loadingClassName="bg-transparent"
                      />
                    </motion.div>
                  </AnimatePresence>

                  {count > 1 && (
                    <>
                      <NavButton
                        label={tCommon("previous")}
                        onClick={() => go(-1)}
                        className="left-5 sm:left-8"
                      >
                        <ArrowLeft className="size-5" />
                      </NavButton>
                      <NavButton
                        label={tCommon("next")}
                        onClick={() => go(1)}
                        className="right-5 sm:right-8"
                      >
                        <ArrowRight className="size-5" />
                      </NavButton>
                    </>
                  )}
                </div>

                {/* Thumbnails */}
                {count > 1 && (
                  <div className="flex justify-center gap-2 px-5 pt-3 pb-6 sm:gap-3 sm:pb-8">
                    {images.map((item, itemIndex) => (
                      <button
                        key={item.url}
                        type="button"
                        onClick={() => jump(itemIndex)}
                        aria-label={t("lightbox.goTo", {
                          index: itemIndex + 1,
                        })}
                        aria-current={itemIndex === index ? "true" : undefined}
                        className="group relative aspect-16/10 w-14 overflow-hidden rounded-md sm:w-20"
                      >
                        <Image
                          src={item.url}
                          alt=""
                          fill
                          sizes="80px"
                          className={cn(
                            "object-cover object-top transition-[opacity,transform] duration-500 ease-out-expo",
                            itemIndex === index
                              ? "opacity-100"
                              : "opacity-40 group-hover:scale-105 group-hover:opacity-75"
                          )}
                        />
                        <span
                          aria-hidden
                          className={cn(
                            "absolute inset-0 rounded-[inherit] opacity-0 ring-1 ring-foreground transition-opacity duration-500 ring-inset",
                            itemIndex === index && "opacity-100"
                          )}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}

// =============================================================================

function NavButton({
  label,
  onClick,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(
        "absolute top-1/2 z-10 hidden size-12 -translate-y-1/2 place-items-center rounded-full border border-foreground/15 bg-surface-1/80 backdrop-blur transition-transform duration-300 ease-out-expo hover:scale-110 active:scale-95 sm:grid",
        className
      )}
    >
      {children}
    </button>
  );
}

// =============================================================================
